<template>
  <div class="slideshow-root" :class="{ fullscreen: isFullscreen }">

    <!-- ── Top Bar ─────────────────────────────────────────────────── -->
    <div class="ss-topbar">
      <div class="ss-topbar-left">
        <div class="ss-logo-wrap" v-if="agency && agency.image_url">
          <img :src="agency.image_url" class="ss-logo"
            onerror="this.onerror=null;this.src='/claimify_logo.png'" />
        </div>
        <div>
          <p class="ss-agency-name mb-0">{{ agency && agency.agency_name }}</p>
          <p class="ss-subtitle mb-0">Analytics Dashboard</p>
        </div>
      </div>

      <div class="ss-topbar-center">
        <span class="ss-slide-label">{{ slides[currentSlide].label }}</span>
        <span class="ss-slide-counter">{{ currentSlide + 1 }} / {{ slides.length }}</span>
      </div>

      <div class="ss-topbar-right">
        <button class="ss-ctrl-btn" @click="togglePause" :title="paused ? 'Resume' : 'Pause'">
          <i :class="paused ? 'fe fe-play' : 'fe fe-pause'"></i>
        </button>
        <button class="ss-ctrl-btn" @click="toggleFullscreen" :title="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'">
          <i :class="isFullscreen ? 'fe fe-minimize' : 'fe fe-maximize'"></i>
        </button>
        <router-link to="/state-dashboard" class="ss-ctrl-btn" title="Back to Dashboard">
          <i class="fe fe-x"></i>
        </router-link>
      </div>
    </div>

    <!-- ── Progress Bar ──────────────────────────────────────────────── -->
    <div class="ss-progress-track">
      <div
        class="ss-progress-fill"
        :key="progressKey"
        :style="{ animationDuration: slideDuration + 'ms', animationPlayState: paused ? 'paused' : 'running' }"
      ></div>
    </div>

    <!-- ── Slides ────────────────────────────────────────────────────── -->
    <div class="ss-stage">

      <div class="ss-blob ss-blob-1"></div>
      <div class="ss-blob ss-blob-2"></div>
      <div class="ss-blob ss-blob-3"></div>

      <transition :name="transitionName" mode="out-in">
        <div class="ss-slide" :key="currentSlide">

          <div class="ss-slide-heading">
            <div class="ss-slide-icon">
              <i :class="slides[currentSlide].icon"></i>
            </div>
            <div>
              <h2 class="ss-slide-title">{{ slides[currentSlide].title }}</h2>
              <p class="ss-slide-desc">{{ slides[currentSlide].desc }}</p>
            </div>
          </div>

          <div class="ss-slide-body">

            <!-- 0 — Formal Sector Stats -->
            <div v-if="currentSlide === 0" class="ss-stats-grid">
              <div class="ss-stat-card" v-for="stat in formalStats" :key="stat.label">
                <div class="ss-stat-icon" :style="{ background: stat.bg }">
                  <i :class="stat.icon" :style="{ color: stat.color }"></i>
                </div>
                <div class="ss-stat-value" :style="{ color: stat.color }"><CountUp :value="stat.value" /></div>
                <div class="ss-stat-label">{{ stat.label }}</div>
              </div>
            </div>

            <!-- 1 — Informal Sector Stats -->
            <div v-if="currentSlide === 1" class="ss-stats-grid">
              <div
                class="ss-stat-card"
                v-for="stat in informalStats"
                :key="stat.label"
                :class="{ 'ss-stat-card-clickable': !!stat.sector }"
                @click="stat.sector && openSectorBreakdown(stat.sector)"
              >
                <div class="ss-stat-icon" :style="{ background: stat.bg }">
                  <i :class="stat.icon" :style="{ color: stat.color }"></i>
                </div>
                <div class="ss-stat-value" :style="{ color: stat.color }"><CountUp :value="stat.value" /></div>
                <div class="ss-stat-label">{{ stat.label }}</div>
                <div class="ss-stat-hint" v-if="stat.sector"><i class="fe fe-chevron-right"></i> View breakdown</div>
              </div>
            </div>

            <!-- 2 — Encounters by LGA -->
            <div v-if="currentSlide === 2" class="ss-chart-wrap">
              <EncounterbyLGA />
            </div>

            <!-- 3 — Informal LGA (BHCPF / SEP by LGA) -->
            <div v-if="currentSlide === 3" class="ss-chart-wrap">
              <InformalLGA />
            </div>

            <!-- 4 — Vulnerable Groups by Category -->
            <div v-if="currentSlide === 4" class="ss-chart-wrap">
              <BasicCategory />
            </div>

            <!-- 5 — Gender by LGA -->
            <div v-if="currentSlide === 5" class="ss-chart-wrap">
              <GenderByLGA :sectors="genderSectors" />
            </div>

            <!-- 6 — Vulnerable Groups by LGA -->
            <div v-if="currentSlide === 6" class="ss-chart-wrap">
              <VulnerableByLGA />
            </div>

            <!-- 7 — Dependents by LGA -->
            <div v-if="currentSlide === 7" class="ss-chart-wrap">
              <DependentsByLGA />
            </div>

            <!-- 8 — Enrollee Distribution by Provider -->
            <div v-if="currentSlide === 8" class="ss-chart-wrap">
              <EnrolleeByLGAAndFacility />
            </div>

            <!-- 9 — Claims / Referrals / HCP Status (3-up) -->
            <div v-if="currentSlide === 9" class="ss-triple-chart">
              <div class="ss-triple-item"><ClaimsCategory /></div>
              <div class="ss-triple-item"><ReferralCategory /></div>
              <div class="ss-triple-item"><ChangeProviderCategory /></div>
            </div>

            <!-- 10 — Claims Financial Summary -->
            <div v-if="currentSlide === 10" class="ss-claims-summary">
              <div class="ss-claims-kpi-row">
                <div class="ss-kpi-card ss-kpi-blue">
                  <div class="ss-kpi-icon"><i class="fe fe-file-text"></i></div>
                  <div class="ss-kpi-value"><CountUp :value="claimsTotalCount" /></div>
                  <div class="ss-kpi-label">Total Claims</div>
                </div>
                <div class="ss-kpi-card ss-kpi-amber">
                  <div class="ss-kpi-icon"><i class="fe fe-credit-card"></i></div>
                  <div class="ss-kpi-value"><CountUp :value="claimsTotalCost" prefix="₦" /></div>
                  <div class="ss-kpi-label">Amount Requested</div>
                </div>
                <div class="ss-kpi-card ss-kpi-green">
                  <div class="ss-kpi-icon"><i class="fe fe-check-circle"></i></div>
                  <div class="ss-kpi-value"><CountUp :value="claimsTotalApproved" prefix="₦" /></div>
                  <div class="ss-kpi-label">Amount Approved</div>
                </div>
              </div>
              <div class="ss-top-providers" v-if="claimProviders.length > 1">
                <p class="ss-section-tag">Top Health Facilities by Claims</p>
                <div class="ss-providers-grid">
                  <div class="ss-provider-row" v-for="(p, i) in claimProviders.slice(0, 6)" :key="i">
                    <span class="ss-provider-rank">#{{ i + 1 }}</span>
                    <span class="ss-provider-name">{{ p.agency_name || '—' }}</span>
                    <span class="ss-provider-count">{{ p.claims_count | numeral(0, 0) }} claims</span>
                    <span class="ss-provider-amount">₦{{ p.total_cost | numeral(0, 0) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 11 — Top Diagnoses Chart -->
            <div v-if="currentSlide === 11" class="ss-chart-wrap">
              <div v-if="diagnosisChartLoading" class="ss-chart-loading">
                <i class="fe fe-loader"></i><p>Loading diagnoses data…</p>
              </div>
              <div v-else-if="!diagnosisSeries[0].data.length" class="ss-chart-empty">
                <i class="fe fe-inbox"></i><p>No diagnoses data available</p>
              </div>
              <apexchart v-else type="bar" height="100%"
                :options="diagnosisChartOptions" :series="diagnosisSeries"
                style="flex:1; min-height:0;"></apexchart>
            </div>

            <!-- 12 — Provider Breakdown Table -->
            <div v-if="currentSlide === 12" class="ss-table-wrap">
              <div class="ss-table-scroll">
                <table class="ss-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Health Facility</th>
                      <th class="text-right">Claims</th>
                      <th class="text-right">Requested</th>
                      <th class="text-right">Vetted</th>
                      <th class="text-right">Approved</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(p, i) in claimProviders.slice(0, claimProviders.length - 1)" :key="i">
                      <td class="ss-td-muted">{{ i + 1 }}</td>
                      <td class="ss-td-name">{{ p.agency_name || '—' }}</td>
                      <td class="text-right"><span class="ss-badge-blue">{{ p.claims_count | numeral(0, 0) }}</span></td>
                      <td class="text-right ss-td-num">₦{{ p.total_cost | numeral(0, 0) }}</td>
                      <td class="text-right ss-td-num">₦{{ p.total_vetted_price | numeral(0, 0) }}</td>
                      <td class="text-right ss-td-green">₦{{ p.total_verified_price | numeral(0, 0) }}</td>
                    </tr>
                    <tr v-if="!claimProviders.length">
                      <td colspan="6" class="text-center ss-td-muted" style="padding:32px;">No data</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 13 — Encounters by Diagnosis -->
            <div v-if="currentSlide === 13" class="ss-chart-wrap">
              <EncounterbyDiagnosis />
            </div>

            <!-- 14 — Claims by Date -->
            <div v-if="currentSlide === 14" class="ss-chart-wrap">
              <ClaimsDate />
            </div>

            <!-- 15 — Encounters by Date -->
            <div v-if="currentSlide === 15" class="ss-chart-wrap">
              <EncountersbyDate />
            </div>

            <!-- 16 — Enrolment Trends (analytics) -->
            <div v-if="currentSlide === 16" class="ss-chart-wrap ss-analytics">
              <EnrollmentTrends />
            </div>

            <!-- 17 — Enrolment by Programme (analytics) -->
            <div v-if="currentSlide === 17" class="ss-chart-wrap ss-analytics">
              <SectorDistribution />
            </div>

            <!-- 18 — Where Enrollees Live (analytics) -->
            <div v-if="currentSlide === 18" class="ss-chart-wrap ss-analytics">
              <EnrolleeMap />
            </div>

            <!-- 19 — Renewals and Attrition (analytics) -->
            <div v-if="currentSlide === 19" class="ss-chart-wrap ss-analytics">
              <RenewalsPanel />
            </div>

            <!-- 20 — Turnaround Times (analytics) -->
            <div v-if="currentSlide === 20" class="ss-chart-wrap ss-analytics">
              <TurnaroundPanel compact />
            </div>

            <!-- 21 — Facility Performance (analytics) -->
            <div v-if="currentSlide === 21" class="ss-chart-wrap ss-analytics">
              <FacilityDashboard />
            </div>

            <!-- 22 — Forecasts (analytics) -->
            <div v-if="currentSlide === 22" class="ss-chart-wrap ss-analytics">
              <div class="row"><div class="col-xl-6"><ForecastPanel fixed-metric="encounters" compact /></div><div class="col-xl-6"><ForecastPanel fixed-metric="claims" compact /></div></div>
            </div>

          </div>
        </div>
      </transition>
    </div>

    <!-- ── Sector Breakdown Modal ───────────────────────────────────── -->
    <transition name="ss-modal-fade">
      <div class="ss-modal-overlay" v-if="breakdown.open" @click.self="closeSectorBreakdown">
        <div class="ss-modal">
          <div class="ss-modal-header">
            <h3>{{ breakdown.sector }}</h3>
            <button class="ss-ctrl-btn" @click="closeSectorBreakdown"><i class="fe fe-x"></i></button>
          </div>
          <div class="ss-modal-body">
            <div v-if="breakdown.loading" class="ss-chart-loading">
              <i class="fe fe-loader"></i><p>Loading breakdown…</p>
            </div>
            <div v-else-if="!breakdown.rows.length" class="ss-chart-empty">
              <i class="fe fe-inbox"></i><p>No breakdown data available</p>
            </div>
            <div v-else class="ss-breakdown-list">
              <div class="ss-breakdown-row" v-for="row in breakdown.rows" :key="row.name">
                <span class="ss-breakdown-name">{{ row.name }}</span>
                <span class="ss-breakdown-count">{{ row.y | numeral(0, 0) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- ── Bottom Nav ────────────────────────────────────────────────── -->
    <div class="ss-bottom-nav">
      <button class="ss-nav-btn" @click="prev" title="Previous">
        <i class="fe fe-chevron-left"></i>
      </button>

      <div class="ss-dots">
        <button
          v-for="(s, i) in slides" :key="i"
          :class="['ss-dot', { active: i === currentSlide }]"
          @click="goTo(i)" :title="s.label"
        ></button>
      </div>

      <button class="ss-nav-btn" @click="next" title="Next">
        <i class="fe fe-chevron-right"></i>
      </button>
    </div>

  </div>
</template>

<script>
import EncounterbyDiagnosis    from "@/views/shis/components/EncounterbyDiagnosis.vue";
import EncounterbyLGA          from "@/views/shis/components/EncounterbyLGA";
import ClaimsDate              from "@/views/shis/components/ClaimsbyDate";
import EncountersbyDate        from "@/views/shis/components/EncountersbyDate";
import ClaimsCategory          from "@/views/shis/components/ClaimStatusChart";
import ReferralCategory        from "@/views/shis/components/ReferralStatusChart";
import ChangeProviderCategory  from "@/views/shis/components/ChangeProviderStatusChart";
import InformalLGA             from "@/views/shis/components/informallga.vue";
import BasicCategory           from "@/views/shis/components/basicCategory.vue";
import GenderByLGA             from "@/views/shis/components/GenderbyLGA.vue";
import VulnerableByLGA         from "@/views/shis/components/VulnerableByLGA.vue";
import DependentsByLGA         from "@/views/shis/components/DependentsByLGAChart.vue";
import EnrolleeByLGAAndFacility from "@/views/shis/components/EnrolleebyLGAandFacility.vue";
import VueApexCharts           from "vue-apexcharts";
import CountUp                 from "@/components/CountUp.vue";
import moment from "moment";
import subProgramsJson from "@/json/sub_programs.json";
import EnrollmentTrends from "@/views/analytics/components/EnrollmentTrends.vue";
import SectorDistribution from "@/views/analytics/components/SectorDistribution.vue";
import EnrolleeMap from "@/views/analytics/components/EnrolleeMap.vue";
import RenewalsPanel from "@/views/analytics/components/RenewalsPanel.vue";
import FacilityDashboard from "@/views/analytics/components/FacilityDashboard.vue";
import ForecastPanel from "@/views/analytics/components/ForecastPanel.vue";
import TurnaroundPanel from "@/views/reports/components/TurnaroundPanel.vue";

let agency = JSON.parse(localStorage.getItem("agency"));
const SLIDE_DURATION = 12000;

export default {
  components: { EnrollmentTrends, SectorDistribution, EnrolleeMap, RenewalsPanel, FacilityDashboard, ForecastPanel, TurnaroundPanel,
    EncounterbyDiagnosis,
    EncounterbyLGA,
    ClaimsDate,
    EncountersbyDate,
    ClaimsCategory,
    ReferralCategory,
    ChangeProviderCategory,
    InformalLGA,
    BasicCategory,
    GenderByLGA,
    VulnerableByLGA,
    DependentsByLGA,
    EnrolleeByLGAAndFacility,
    apexchart: VueApexCharts,
    CountUp,
  },

  data() {
    return {
      agency,
      user: null,
      currentSlide: 0,
      paused: false,
      isFullscreen: false,
      transitionName: "slide-left",
      progressKey: 0,
      slideDuration: SLIDE_DURATION,
      timer: null,
      formaldata: null,
      informaldata: null,
      genderSectors: [
        { name: "State Civil Servant" },
        { name: "State Public Servant" },
      ],
      claimProviders: [{ claims_count: 0, total_cost: 0, total_vetted_price: 0, total_verified_price: 0 }],
      diagnosisChartLoading: false,
      diagnosisCategories: [],
      diagnosisSeries: [{ name: "Claims", data: [] }],
      claimsFrom: moment().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
      claimsTo:   moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      breakdown: { open: false, loading: false, sector: "", rows: [] },
      wasPausedBeforeBreakdown: false,
      slides: [
        { label: "Formal Sector",            title: "Formal Sector Enrollees",              desc: "State civil servants, dependants, LGA staff and T-SHIP beneficiaries",           icon: "fe fe-briefcase"   },
        { label: "Informal Sector",           title: "Informal Sector Enrollees",            desc: "BHCPF, State Equity Programme and voluntary contributors",                       icon: "fe fe-users"       },
        { label: "Encounters by LGA",         title: "Encounters by Local Govt. Area",       desc: "Distribution of healthcare encounters across LGAs",                             icon: "fe fe-map-pin"     },
        { label: "Informal LGA",              title: "Informal Sector Enrollment by LGA",    desc: "BHCPF and State Equity Program enrollees distributed by local government area",  icon: "fe fe-grid"        },
        { label: "Vulnerable Categories",     title: "Vulnerable Groups by Category",        desc: "Breakdown of vulnerable group enrollees across programme categories",            icon: "fe fe-pie-chart"   },
        { label: "Gender by LGA",             title: "Gender Distribution by LGA",           desc: "Male and female enrollees distributed across local government areas",            icon: "fe fe-bar-chart-2" },
        { label: "Vulnerable by LGA",         title: "Vulnerable Groups by LGA",             desc: "Pregnant women, children under 5, aged, special needs and poorest of the poor", icon: "fe fe-heart"       },
        { label: "Dependents by LGA",         title: "Formal Sector Dependents by LGA",      desc: "Dependents of civil servants distributed across LGAs with drilldown to wards",  icon: "fe fe-users"       },
        { label: "Enrollee by Provider",      title: "Enrollee Distribution by Provider",    desc: "Enrollees mapped to health facilities per LGA with facility-level drilldown",   icon: "fe fe-activity"    },
        { label: "Status Distributions",      title: "Claims · Referrals · HCP Requests",   desc: "Current status breakdown across three key service categories",                   icon: "fe fe-pie-chart"   },
        { label: "Claims Summary",            title: "Claims Financial Summary",             desc: "Total claims volume, amounts requested and approved across all providers",       icon: "fe fe-dollar-sign" },
        { label: "Top Diagnoses",             title: "Top 30 Diagnoses (Claims)",            desc: "Most frequently claimed diagnoses ranked by volume",                             icon: "fe fe-thermometer" },
        { label: "Provider Breakdown",        title: "Claims by Health Facility",            desc: "Per-provider breakdown of claims count and financial amounts",                   icon: "fe fe-list"        },
        { label: "Encounters by Dx",          title: "Encounters by Diagnosis",              desc: "Top ICD-coded diagnoses driving healthcare utilisation",                         icon: "fe fe-activity"    },
        { label: "Claims Trend",              title: "Claims Submitted by Date",             desc: "Monthly trend of submitted insurance claims",                                    icon: "fe fe-trending-up" },
        { label: "Encounters Trend",          title: "Encounters Over Time",                 desc: "Volume of healthcare encounters recorded over time",                             icon: "fe fe-bar-chart-2" },
        { label: "Enrolment Trends", title: "Enrolment Trends", desc: "New enrollees per week, month or year, by programme, with the running total", icon: "fe fe-trending-up" },
        { label: "Programmes", title: "Enrolment by Programme", desc: "Principals and dependants in each programme", icon: "fe fe-bar-chart-2" },
        { label: "Enrollee Map", title: "Where Enrollees Live", desc: "Enrollees per LGA, with drill-down to wards", icon: "fe fe-map" },
        { label: "Renewals", title: "Renewals and Attrition", desc: "Covers due each month, renewals, renewal and attrition rates", icon: "fe fe-refresh-cw" },
        { label: "Turnaround", title: "Turnaround Times", desc: "Authorization codes and claims against their targets", icon: "fe fe-clock" },
        { label: "Facility Performance", title: "Facility Performance", desc: "Utilization, referrals, claims and ratings by facility, as counts or per enrollee", icon: "fe fe-home" },
        { label: "Forecasts", title: "Forecasts", desc: "Expected encounters and claims for the coming months", icon: "fe fe-activity" },
      ],
    };
  },

  computed: {
    formalStats() {
      const d = this.formaldata || {};
      const total =
        (d.state_civil || 0) + (d.dependants_dynamic || 0) +
        (d.public_servant || 0) + (d.dependants_public_servants || 0) +
        (d.t_ship || 0) + (d.lga_civil || 0) + (d.dependants_lga_civil_servants || 0);
      return [
        { label: "State Civil Servants",        value: d.state_civil || 0,                   icon: "fe fe-user-plus", color: "#60a5fa", bg: "rgba(37,99,235,.26)"  },
        { label: "Dependants (Civil Servants)",  value: d.dependants_dynamic || 0,            icon: "fe fe-users",     color: "#a78bfa", bg: "rgba(124,58,237,.26)" },
        { label: "T-SHIP",                       value: d.t_ship || 0,                        icon: "fe fe-shield",    color: "#22d3ee", bg: "rgba(8,145,178,.26)"  },
        { label: "LGA Civil Servants",           value: d.lga_civil || 0,                     icon: "fe fe-map-pin",   color: "#34d399", bg: "rgba(5,150,105,.26)"  },
        { label: "Dependants (LGA CS)",          value: d.dependants_lga_civil_servants || 0, icon: "fe fe-users",     color: "#fbbf24", bg: "rgba(217,119,6,.26)"  },
        { label: "Total Formal Enrollees",       value: total,                                 icon: "fe fe-star",      color: "#f87171", bg: "rgba(220,38,38,.26)"  },
        { label: "All Enrollees (Incl. Inf.)",   value: d.enrollees || 0,                     icon: "fe fe-award",     color: "#60a5fa", bg: "rgba(37,99,235,.32)"  },
      ];
    },
    informalStats() {
      const d = this.informaldata || {};
      return [
        { label: "BHCPF",                value: d.bhcpf || 0,                             icon: "fe fe-heart",     color: "#60a5fa", bg: "rgba(37,99,235,.26)"  },
        { label: "State Equity Program", value: d.sep || 0,                               icon: "fe fe-shield",    color: "#34d399", bg: "rgba(5,150,105,.26)", sector: "State Equity Program" },
        { label: "Voluntary Contrib.",   value: (d.voluntary || 0) + (d.dependants || 0), icon: "fe fe-users",     color: "#a78bfa", bg: "rgba(124,58,237,.26)" },
        { label: "Health Facilities",    value: d.providers || 0,                         icon: "fe fe-activity",  color: "#22d3ee", bg: "rgba(8,145,178,.26)"  },
        { label: "Claims",               value: d.claims || 0,                            icon: "fe fe-file-text", color: "#fbbf24", bg: "rgba(217,119,6,.26)"  },
        { label: "Auth. Codes",          value: d.referrals || 0,                         icon: "fe fe-send",      color: "#f87171", bg: "rgba(220,38,38,.26)"  },
      ];
    },
    claimsTotalCount() {
      if (!this.claimProviders.length) return 0;
      return Number(this.claimProviders[this.claimProviders.length - 1].claims_count) || 0;
    },
    claimsTotalCost() {
      if (!this.claimProviders.length) return 0;
      return Number(this.claimProviders[this.claimProviders.length - 1].total_cost) || 0;
    },
    claimsTotalApproved() {
      if (!this.claimProviders.length) return 0;
      return Number(this.claimProviders[this.claimProviders.length - 1].total_verified_price) || 0;
    },
    diagnosisChartOptions() {
      return {
        chart: { type: "bar", toolbar: { show: false }, fontFamily: "inherit", background: "transparent", foreColor: "rgba(255,255,255,0.7)" },
        plotOptions: { bar: { horizontal: true, borderRadius: 6, barHeight: "72%", dataLabels: { position: "center" } } },
        dataLabels: { enabled: true, style: { fontSize: "13px", fontWeight: 800, colors: ["#fff"] } },
        xaxis: { categories: this.diagnosisCategories, labels: { style: { fontSize: "14px", fontWeight: 600, colors: "rgba(255,255,255,0.85)" } } },
        yaxis: { labels: { style: { fontSize: "14px", fontWeight: 600, colors: "rgba(255,255,255,0.92)" }, maxWidth: 280 } },
        colors: ["#3b82f6"],
        fill: { type: "gradient", gradient: { shade: "dark", type: "horizontal", gradientToColors: ["#8b5cf6"], stops: [0, 100] } },
        grid: { borderColor: "rgba(255,255,255,0.1)", strokeDashArray: 4 },
        tooltip: { theme: "dark", y: { formatter: v => v.toLocaleString() + " claims" } },
      };
    },
  },

  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.axios.get(`/api/v1/auth/getformalsectorenrollees/${agency.id}`)
      .then(r => { this.formaldata = r.data; }).catch(console.error);
    this.axios.get(`/api/v1/auth/getinformalsectorenrollees/${agency.id}`)
      .then(r => { this.informaldata = r.data; }).catch(console.error);
    this.getClaimsStats();
    this.getDiagnosesChart();
  },

  mounted() {
    this.startTimer();
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    this.enterFullscreen();
  },

  beforeDestroy() {
    this.stopTimer();
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
  },

  methods: {
    getClaimsStats() {
      this.axios.post(`/api/v1/auth/claims/statistics`, {
        agency_id: agency.id,
        from: this.claimsFrom,
        to: this.claimsTo,
      }).then(r => {
        this.claimProviders = r.data.data.filter(p => p.total_cost > 0);
      }).catch(console.error);
    },
    getDiagnosesChart() {
      this.diagnosisChartLoading = true;
      this.axios.post(`/api/v1/auth/claims/statistics/diagnoses`, {
        agency_id: agency.id,
        from: this.claimsFrom,
        to: this.claimsTo,
      }).then(r => {
        const sorted = (r.data.data || [])
          .slice().sort((a, b) => Number(b.total_claims || 0) - Number(a.total_claims || 0))
          .slice(0, 30);
        this.diagnosisCategories = sorted.map(d => d.diagnosis_name || "Unknown");
        this.diagnosisSeries = [{ name: "Claims", data: sorted.map(d => Number(d.total_claims || 0)) }];
        this.diagnosisChartLoading = false;
      }).catch(e => { console.error(e); this.diagnosisChartLoading = false; });
    },
    openSectorBreakdown(sector) {
      this.wasPausedBeforeBreakdown = this.paused;
      this.paused = true;
      this.breakdown = { open: true, loading: true, sector, rows: [] };
      const subPrograms = (subProgramsJson[agency.id] && subProgramsJson[agency.id]["Vulnerable Sponsored"]) || [];

      Promise.all(
        subPrograms.map((name) =>
          this.axios
            .get(`/api/v1/auth/getUsersfilterparams`, {
              params: { agency_id: agency.id, sectorType: name, perPage: 1 },
            })
            .then((response) => ({
              name,
              y: (response.data && response.data.meta && response.data.meta.total_principal) || 0,
            }))
            .catch((error) => {
              console.error(error);
              return { name, y: 0 };
            })
        )
      ).then((rows) => {
        this.breakdown.rows = rows;
        this.breakdown.loading = false;
      });
    },
    closeSectorBreakdown() {
      this.breakdown.open = false;
      this.paused = this.wasPausedBeforeBreakdown;
    },
    startTimer() {
      this.stopTimer();
      this.timer = setInterval(() => { if (!this.paused) this.next(); }, SLIDE_DURATION);
    },
    stopTimer() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    },
    next() {
      this.transitionName = "slide-left";
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      this.progressKey++;
      this.startTimer();
    },
    prev() {
      this.transitionName = "slide-right";
      this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
      this.progressKey++;
      this.startTimer();
    },
    goTo(i) {
      this.transitionName = i > this.currentSlide ? "slide-left" : "slide-right";
      this.currentSlide = i;
      this.progressKey++;
      this.startTimer();
    },
    togglePause() { this.paused = !this.paused; },
    enterFullscreen() {
      const el = this.$el;
      if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    },
    toggleFullscreen() {
      if (document.fullscreenElement) document.exitFullscreen();
      else this.enterFullscreen();
    },
    onFullscreenChange() { this.isFullscreen = !!document.fullscreenElement; },
  },
};
</script>

<style scoped>
.ss-analytics { background: #f8fafc; border-radius: 14px; padding: 16px; color: #0f172a; max-height: 72vh; overflow-y: auto; }
.slideshow-root {
  position: fixed; inset: 0; z-index: 9999;
  display: flex; flex-direction: column;
  background: #040d1a; color: #fff;
  font-family: Inter, Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  overflow: hidden;
}

/* ── Top Bar ── */
.ss-topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 24px;
  background: rgba(255,255,255,0.04);
  border-bottom: 1px solid rgba(255,255,255,0.07);
  backdrop-filter: blur(8px); flex-shrink: 0; z-index: 10; gap: 16px;
}
.ss-topbar-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
.ss-logo-wrap { background: #fff; border-radius: 10px; padding: 6px 10px; box-shadow: 0 4px 16px rgba(0,0,0,0.35); display: flex; align-items: center; flex-shrink: 0; }
.ss-logo { height: 46px; width: auto; display: block; }
.ss-agency-name { font-size: 1.1rem; font-weight: 800; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ss-subtitle { font-size: 0.74rem; font-weight: 600; color: rgba(255,255,255,0.7); text-transform: uppercase; letter-spacing: 0.08em; }
.ss-topbar-center { display: flex; align-items: center; gap: 10px; flex: 1; justify-content: center; }
.ss-slide-label { font-size: 0.92rem; font-weight: 800; color: #fff; text-transform: uppercase; letter-spacing: 0.08em; }
.ss-slide-counter { font-size: 0.8rem; font-weight: 700; background: rgba(255,255,255,0.14); border-radius: 20px; padding: 3px 12px; color: rgba(255,255,255,0.85); }
.ss-topbar-right { display: flex; align-items: center; gap: 8px; }
.ss-ctrl-btn {
  width: 34px; height: 34px;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px; color: rgba(255,255,255,0.75);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; text-decoration: none; font-size: 0.9rem;
  transition: background 0.15s, color 0.15s;
}
.ss-ctrl-btn:hover { background: rgba(255,255,255,0.15); color: #fff; text-decoration: none; }

/* ── Progress ── */
.ss-progress-track { height: 4px; background: rgba(255,255,255,0.08); flex-shrink: 0; }
.ss-progress-fill {
  height: 100%; width: 0;
  background: linear-gradient(90deg, #22d3ee, #3b82f6, #a855f7);
  box-shadow: 0 0 12px rgba(59,130,246,0.7);
  animation: ss-progress linear forwards;
}
@keyframes ss-progress { from { width: 0%; } to { width: 100%; } }

/* ── Stage ── */
.ss-stage { flex: 1; position: relative; overflow: hidden; display: flex; flex-direction: column; }
.ss-blob { position: absolute; border-radius: 50%; filter: blur(90px); opacity: 0.3; pointer-events: none; }
.ss-blob-1 { width: 600px; height: 600px; background: #2563eb; top: -200px; left: -150px; }
.ss-blob-2 { width: 500px; height: 500px; background: #7c3aed; bottom: -180px; right: -120px; }
.ss-blob-3 { width: 350px; height: 350px; background: #0891b2; top: 40%; left: 45%; }

/* ── Slide ── */
.ss-slide { position: absolute; inset: 0; display: flex; flex-direction: column; padding: 28px 36px 16px; overflow: hidden; }
.ss-slide-heading { display: flex; align-items: center; gap: 16px; margin-bottom: 22px; flex-shrink: 0; }
.ss-slide-icon {
  width: 64px; height: 64px; border-radius: 18px;
  background: linear-gradient(135deg, #2563eb, #7c3aed); border: none;
  box-shadow: 0 8px 24px rgba(59,130,246,0.45);
  display: flex; align-items: center; justify-content: center;
  font-size: 1.6rem; color: #fff; flex-shrink: 0;
}
.ss-slide-title { font-size: 2.3rem; font-weight: 900; color: #fff; margin: 0 0 4px; line-height: 1.1; letter-spacing: -0.02em; }
.ss-slide-desc { font-size: 1.05rem; font-weight: 500; color: rgba(255,255,255,0.78); margin: 0; }
.ss-slide-body { flex: 1; overflow: hidden; display: flex; flex-direction: column; }

/* ── Stats Grid ── */
.ss-stats-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 20px; align-content: start; }
.ss-stat-card {
  background: linear-gradient(160deg, rgba(255,255,255,0.11), rgba(255,255,255,0.04));
  border: 1px solid rgba(255,255,255,0.14); border-top: 3px solid rgba(255,255,255,0.35);
  box-shadow: 0 12px 32px rgba(0,0,0,0.35);
  border-radius: 18px; padding: 24px 22px;
  display: flex; flex-direction: column; gap: 12px;
  transition: background 0.2s; backdrop-filter: blur(6px);
}
.ss-stat-card:hover { background: rgba(255,255,255,0.14); }
.ss-stat-icon { width: 54px; height: 54px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 1.45rem; }
.ss-stat-value { font-size: 3rem; font-weight: 900; color: #fff; line-height: 1; letter-spacing: -0.03em; text-shadow: 0 2px 18px rgba(0,0,0,0.35); }
.ss-stat-label { font-size: 0.86rem; font-weight: 700; color: rgba(255,255,255,0.85); text-transform: uppercase; letter-spacing: 0.05em; line-height: 1.4; }
.ss-stat-card-clickable { cursor: pointer; }
.ss-stat-card-clickable:hover { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.18); }
.ss-stat-hint { font-size: 0.78rem; font-weight: 600; color: rgba(255,255,255,0.65); display: flex; align-items: center; gap: 2px; }

/* ── Sector Breakdown Modal ── */
.ss-modal-overlay {
  position: fixed; inset: 0; z-index: 20;
  background: rgba(0,0,0,0.6); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; padding: 24px;
}
.ss-modal {
  width: 100%; max-width: 480px; max-height: 80vh;
  background: #0a1428; border: 1px solid rgba(255,255,255,0.1); border-radius: 18px;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
}
.ss-modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 20px; border-bottom: 1px solid rgba(255,255,255,0.08); flex-shrink: 0;
}
.ss-modal-header h3 { margin: 0; font-size: 1.05rem; font-weight: 800; color: #fff; }
.ss-modal-body { padding: 18px 20px; overflow-y: auto; }
.ss-breakdown-list { display: flex; flex-direction: column; gap: 8px; }
.ss-breakdown-row {
  display: flex; align-items: center; justify-content: space-between;
  background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px; padding: 12px 14px;
}
.ss-breakdown-name { font-size: 0.98rem; font-weight: 600; color: #fff; }
.ss-breakdown-count { font-size: 1.15rem; font-weight: 900; color: #34d399; }
.ss-modal-fade-enter-active, .ss-modal-fade-leave-active { transition: opacity 0.2s ease; }
.ss-modal-fade-enter, .ss-modal-fade-leave-to { opacity: 0; }

/* ── Chart Wrap ── */
.ss-chart-wrap {
  flex: 1; overflow: hidden;
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 18px; padding: 18px;
  display: flex; flex-direction: column;
}
.ss-chart-wrap > * { flex: 1; }
.ss-chart-loading, .ss-chart-empty {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  color: rgba(255,255,255,0.3); gap: 10px;
}
.ss-chart-loading i, .ss-chart-empty i { font-size: 2rem; }
.ss-chart-loading p, .ss-chart-empty p { font-size: 1rem; margin: 0; }

/* Force Highcharts inside chart-wrap to look reasonable on dark bg */
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-background) { fill: transparent; }
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-title),
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-subtitle),
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-axis-title),
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-axis-labels text),
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-legend-item text),
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-data-label text) { fill: rgba(255,255,255,0.92) !important; color: rgba(255,255,255,0.92) !important; }
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-grid-line) { stroke: rgba(255,255,255,0.1); }

/* Projector-sized chart text and marks */
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-title) { font-size: 22px !important; font-weight: 800 !important; }
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-axis-labels text),
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-legend-item text) { font-size: 15px !important; font-weight: 600 !important; }
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-data-label text) { font-size: 15px !important; font-weight: 800 !important; }
.ss-chart-wrap:not(.ss-analytics) :deep(.highcharts-graph) { stroke-width: 4.5px; }
.ss-chart-wrap:not(.ss-analytics) :deep(.apexcharts-text),
.ss-chart-wrap:not(.ss-analytics) :deep(.apexcharts-legend-text) { font-size: 14px !important; font-weight: 600 !important; }

/* ── Triple Chart ── */
.ss-triple-chart { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; flex: 1; overflow: hidden; }
.ss-triple-item {
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 16px; padding: 12px; overflow: hidden; display: flex; flex-direction: column;
}
.ss-triple-item > * { flex: 1; }
.ss-triple-item :deep(.highcharts-background) { fill: transparent; }
.ss-triple-item :deep(.highcharts-title),
.ss-triple-item :deep(.highcharts-legend-item text),
.ss-triple-item :deep(.highcharts-data-label text) { fill: rgba(255,255,255,0.92) !important; font-size: 14px !important; font-weight: 700 !important; }
.ss-triple-item :deep(.highcharts-title) { font-size: 19px !important; font-weight: 800 !important; }

/* ── Claims Summary ── */
.ss-claims-summary { flex: 1; display: flex; flex-direction: column; gap: 20px; overflow: hidden; }
.ss-claims-kpi-row { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; flex-shrink: 0; }
.ss-kpi-card { border-radius: 20px; padding: 30px 26px; display: flex; flex-direction: column; gap: 14px; border: 1px solid rgba(255,255,255,0.16); box-shadow: 0 14px 36px rgba(0,0,0,0.35); }
.ss-kpi-blue  { background: linear-gradient(135deg, rgba(37,99,235,0.75),  rgba(37,99,235,0.25));  }
.ss-kpi-amber { background: linear-gradient(135deg, rgba(217,119,6,0.75),  rgba(217,119,6,0.25));  }
.ss-kpi-green { background: linear-gradient(135deg, rgba(5,150,105,0.75),  rgba(5,150,105,0.25));  }
.ss-kpi-icon { font-size: 2rem; opacity: 1; }
.ss-kpi-value { font-size: 3.1rem; font-weight: 900; color: #fff; line-height: 1; letter-spacing: -0.03em; text-shadow: 0 2px 18px rgba(0,0,0,0.3); }
.ss-kpi-label { font-size: 0.92rem; font-weight: 700; color: rgba(255,255,255,0.9); text-transform: uppercase; letter-spacing: 0.07em; }
.ss-top-providers { flex: 1; overflow: hidden; display: flex; flex-direction: column; }
.ss-section-tag { font-size: 0.85rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; color: rgba(255,255,255,0.75); margin-bottom: 10px; }
.ss-providers-grid { display: flex; flex-direction: column; gap: 8px; overflow-y: auto; }
.ss-provider-row {
  display: grid; grid-template-columns: 32px 1fr auto auto;
  align-items: center; gap: 12px;
  background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 12px; padding: 12px 16px;
}
.ss-provider-rank { font-size: 0.9rem; color: rgba(255,255,255,0.7); font-weight: 800; }
.ss-provider-name { font-size: 1rem; font-weight: 700; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ss-provider-count { font-size: 0.88rem; font-weight: 600; color: rgba(255,255,255,0.75); white-space: nowrap; }
.ss-provider-amount { font-size: 1.05rem; font-weight: 800; color: #60a5fa; white-space: nowrap; }

/* ── Provider Table ── */
.ss-table-wrap { flex: 1; overflow: hidden; display: flex; flex-direction: column; }
.ss-table-scroll {
  flex: 1; overflow-y: auto;
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 16px;
}
.ss-table-scroll::-webkit-scrollbar { width: 5px; }
.ss-table-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.04); }
.ss-table-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 4px; }
.ss-table { width: 100%; border-collapse: collapse; }
.ss-table thead th {
  position: sticky; top: 0;
  background: rgba(10,20,45,0.95); backdrop-filter: blur(6px);
  padding: 12px 16px; text-align: left;
  font-size: 0.8rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.07em;
  color: rgba(255,255,255,0.75); border-bottom: 1px solid rgba(255,255,255,0.12);
}
.ss-table thead th.text-right { text-align: right; }
.ss-table tbody tr { border-bottom: 1px solid rgba(255,255,255,0.05); transition: background 0.15s; }
.ss-table tbody tr:hover { background: rgba(255,255,255,0.04); }
.ss-table tbody td { padding: 12px 16px; font-size: 0.98rem; }
.ss-table td.text-right { text-align: right; }
.ss-td-muted { color: rgba(255,255,255,0.6); font-size: 0.85rem !important; font-weight: 700; }
.ss-td-name { font-weight: 700; color: #fff; }
.ss-td-num { color: rgba(255,255,255,0.88); font-weight: 600; }
.ss-td-green { color: #34d399; font-weight: 800; }
.ss-badge-blue { display: inline-block; padding: 3px 12px; border-radius: 20px; font-size: 0.88rem; font-weight: 800; background: rgba(37,99,235,0.4); color: #dbeafe; }

/* ── Bottom Nav ── */
.ss-bottom-nav {
  display: flex; align-items: center; justify-content: center; gap: 16px; padding: 10px 24px;
  background: rgba(255,255,255,0.03); border-top: 1px solid rgba(255,255,255,0.06); flex-shrink: 0;
}
.ss-nav-btn {
  width: 36px; height: 36px;
  background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 50%; color: rgba(255,255,255,0.7);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 1rem; transition: background 0.15s, color 0.15s;
}
.ss-nav-btn:hover { background: rgba(255,255,255,0.15); color: #fff; }
.ss-dots { display: flex; align-items: center; gap: 6px; }
.ss-dot {
  height: 8px; width: 8px; border-radius: 4px;
  background: rgba(255,255,255,0.4); border: none; cursor: pointer; padding: 0;
  transition: all 0.35s cubic-bezier(.4,0,.2,1);
}
.ss-dot.active { width: 34px; background: linear-gradient(90deg, #3b82f6, #a855f7); box-shadow: 0 0 10px rgba(99,102,241,0.7); }

/* ── Transitions ── */
.slide-left-enter-active, .slide-left-leave-active,
.slide-right-enter-active, .slide-right-leave-active {
  transition: opacity 0.45s ease, transform 0.45s cubic-bezier(.4,0,.2,1);
}
.slide-left-enter    { opacity: 0; transform: translateX(60px);  }
.slide-left-leave-to { opacity: 0; transform: translateX(-60px); }
.slide-right-enter   { opacity: 0; transform: translateX(-60px); }
.slide-right-leave-to { opacity: 0; transform: translateX(60px); }
</style>
